import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { roomAtom } from "../../core/atoms";
import type { ContainerComponentProps } from "../../core/types";

/**
 * Zoom recommends using a single <video-player-container> parent element,
 * for all user video components (<video-player>).
 * - Screen share based <video-player> should get their own <video-player-container>.
 *
 * This is supposed to optimize performance:
 * https://devforum.zoom.us/t/should-video-player-be-a-direct-child-of-video-player-container-help-needed/125828/4
 *
 * Base styling on the web component that can only be overridden via it's style attribute.
 *
 * Firefox/Brave fix: Zoom uses canvas in its shadow DOM on these browsers.
 * Slotted content backgrounds paint over the canvas due to shadow DOM stacking.
 * We override canvas z-index so video renders above cell backgrounds.
 */
export function ZoomVideoContainer({
  className,
  dataTestId,
  styleOverride,
  children,
}: ContainerComponentProps) {
  const room = useAtomValue(roomAtom);
  const containerRef = useRef<HTMLElement | null>(null);

  // set up observer when shadowRoot is most likely ready
  useEffect(() => {
    if (!room || !containerRef.current) return;

    const shadowRoot = containerRef.current.shadowRoot;
    if (!shadowRoot) return;

    // watch for new canvas elements added to shadow DOM
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const addedNode of mutation.addedNodes) {
          if (addedNode instanceof HTMLCanvasElement) {
            // directly modify the element's style
            addedNode.style.zIndex = "1";
          }
        }
      }
    });

    observer.observe(shadowRoot, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [room]);

  return (
    <video-player-container
      ref={containerRef}
      class={className}
      style={styleOverride}
      data-testid={dataTestId}
    >
      {children}
    </video-player-container>
  );
}
